import React, { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { holdLabel } from '../lib/hold-codes.js';

/**
 * Shared helpers for the read-only account-overview drill-down screens
 * (SCRN 44/45/52 — CCF-110/111/109). They all fetch the same
 * `POST /api/accounts/details` open object and render label/value fields; only
 * the field set and layout differ by account type, so the fetch hook and the
 * presentational primitives live here.
 */

/** Open account-detail shape from `/accounts/details`. Account-type-branched
 * field subsets are read off this by each screen. */
export interface AccountDetail {
  Found?: boolean;
  AccountNumber?: string;
  AccountType?: string;
  Nickname?: string;
  Balance?: number;
  AvailableBalance?: number;
  Currency?: string;
  Status?: string;
  Iban?: string;
  Bic?: string;
  Address?: string;
  OpenedDate?: string;
  ClosedDate?: string;
  ChequeMgmtHold?: number;
  /**
   * Per-account holds threaded onto the detail from the `customer/accounts/list`
   * overlay (issue #224) — the same structured shape the dashboard tile reads.
   * Each carries a hold `Code` (letter or numeric); the "Account holds" field
   * renders `<code> — <explanation>` via {@link accountHoldsText}.
   */
  Holds?: Array<{ Code?: string; Comments?: string; Reason?: string }>;
  HoldCount?: number;
  /**
   * The `account/information` hold indicator (Y/N). A bare flag — it does NOT
   * carry the hold codes — so it is only the fallback when the structured
   * `Holds` array is absent (issue #224).
   */
  CustomerHoldIndicator?: string;
  [key: string]: unknown;
}

/**
 * The "Account holds" field value for an account-overview screen (issue #224).
 *
 * Prefers the structured per-account `Holds` array (threaded from the
 * `customer/accounts/list` overlay — the same source the dashboard tile reads)
 * and renders each hold as `<code> — <comment>` where the comment is the
 * per-hold free-text the gateway returns for that hold (issue #465, @dylan219
 * 2026-07-22 — the same adjustment applied to the dashboard tile; adjusts #108
 * on the hold surfaces, see #108/#426). When a hold carries no comment it falls
 * back to the Defined Account Holds & Indicators catalogue meaning, so the
 * drill-down "Account holds" field is consistent with the dashboard and never
 * shows a bare, unexplained code. When the account carries no holds it shows
 * "No holds"; when the structured array is absent it falls back to the raw
 * `CustomerHoldIndicator` flag.
 */
export function accountHoldsText(detail: AccountDetail): string {
  const holds = detail.Holds;
  if (Array.isArray(holds)) {
    const labels = holds
      .map((h) => holdLabel(h?.Code, h?.Comments ?? h?.Reason))
      .filter((l) => l.length > 0);
    if (labels.length > 0) return labels.join('; ');
    // Empty structured array (and no fallback indicator) ⇒ no holds.
    if (!detail.CustomerHoldIndicator) return 'No holds';
  }
  return text(detail.CustomerHoldIndicator);
}

export function formatCurrency(amount?: unknown, currency = 'EUR'): string {
  if (typeof amount !== 'number') return '—';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export function formatDate(iso?: unknown): string {
  if (typeof iso !== 'string' || !iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** Reads an open-object field as a display string, '—' when absent. */
export function text(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

export function statusLabel(status?: string): string {
  if (status === 'ACTIVE') return 'Active';
  if (status === 'DORMANT') return 'Dormant';
  if (status === 'CLOSED') return 'Closed';
  return status ?? '—';
}

/** A single read-only label-above-value field, faithful to the export. */
export function Field({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
        {label}
      </p>
      <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
        {value ?? '—'}
      </p>
    </div>
  );
}

/** A vertical "label: value" row used in the additional-information sections. */
export function Row({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex justify-between gap-4">
      <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
        {label}
      </p>
      <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 text-right">
        {value ?? '—'}
      </p>
    </div>
  );
}

/** A bordered, headed read-only section (vertical detail columns). */
export function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="border-2 border-[#dad7d3] bg-white p-6">
      <h2 className="text-[20px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6 m-0">
        {title}
      </h2>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
}

/** Fetch state for an account-detail screen. */
export interface AccountDetailState {
  detail: AccountDetail | null;
  loading: boolean;
  error: string;
}

/**
 * Loads an account-detail endpoint for the account in the `?account=` query
 * param, against the verified customer + Teller ID in context. Shared by the
 * overview screens. `endpoint` selects the BFF path — `/accounts/details` for
 * the current/savings + deposit overviews, `/termloan/details` for the Term
 * Loan Information screen (SCRN 44 / CCF-110, issue #329, its own R4 op).
 */
function useDetail(
  accountNumber: string,
  endpoint: '/accounts/details' | '/termloan/details',
): AccountDetailState {
  const { customer } = useCustomer();
  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';

  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      if (!accountNumber) {
        setError('No account selected.');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const {
          data,
          error: apiError,
          response,
        } = await api.POST(endpoint, {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber, accountNumber },
        });
        if (cancelled) return;
        if (apiError || !data) {
          setError(
            response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : `Could not load account (${response.status}).`,
          );
          return;
        }
        const acct = data as unknown as AccountDetail;
        if (acct.Found === false) {
          setError('Account not found.');
          return;
        }
        setDetail(acct);
      } catch {
        if (!cancelled) setError('Request failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId, accountNumber, endpoint]);

  return { detail, loading, error };
}

/**
 * Loads `POST /api/accounts/details` for the current/savings + deposit overview
 * screens (SCRN 45/52).
 */
export function useAccountDetail(accountNumber: string): AccountDetailState {
  return useDetail(accountNumber, '/accounts/details');
}

/**
 * The customer's **CIF address** (customer master address) for the "Address"
 * field on the account-overview screens (issue #603, extended to every
 * account-information page by #638).
 *
 * Sourced from `/customers/dashboard` → `info.CustomerAddressLine1..5` +
 * `CustomerEircode` — the SAME source the CCF-74 Statements screen uses for its
 * "CIF" address column (@dylan219 Option A), NOT the per-account Postal Address
 * (`detail.Address`). Every account-information overview (current/savings SCRN-45,
 * deposit SCRN-52, term loan SCRN-44) renders this so the shown address is
 * consistent across all account-information pages.
 *
 * The loaded value is KEYED to the `{ customerNumber, tellerId }` context it was
 * fetched for, and the caller compares that key at render time — so a stale value
 * from a prior customer never renders (no cross-customer PII flash) even for the
 * render before the effect fires (see #603). Non-fatal: a lookup miss or absent
 * context yields '' so the field renders '—' rather than blocking the view.
 */
export function useCifAddress(): string {
  const { customer } = useCustomer();
  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  const [loaded, setLoaded] = useState<{
    customerNumber: string;
    tellerId: string;
    value: string;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadCifAddress(): Promise<void> {
      if (!hasContext) return;
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/dashboard',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled || apiError || !data) return;
        const info = (data.info ?? {}) as Record<string, unknown>;
        const str = (v: unknown): string =>
          typeof v === 'string' ? v.trim() : '';
        const lines = [
          str(info.CustomerAddressLine1),
          str(info.CustomerAddressLine2),
          str(info.CustomerAddressLine3),
          str(info.CustomerAddressLine4),
          str(info.CustomerAddressLine5),
          str(info.CustomerEircode),
        ].filter(Boolean);
        if (cancelled) return;
        setLoaded({ customerNumber, tellerId, value: lines.join(', ') });
      } catch {
        // Non-fatal — the Address field renders '—' when the CIF address is absent.
      }
    }
    void loadCifAddress();
    return () => {
      cancelled = true;
    };
  }, [customerNumber, tellerId, hasContext]);

  return loaded &&
    loaded.customerNumber === customerNumber &&
    loaded.tellerId === tellerId
    ? loaded.value
    : '';
}

/**
 * Loads `POST /api/termloan/details` — the dedicated R4 term-loan servicing op
 * (SCRN 44 / CCF-110, issue #329) — for the Term Loan Information screen.
 */
export function useTermLoanDetail(accountNumber: string): AccountDetailState {
  return useDetail(accountNumber, '/termloan/details');
}
